import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react"
import { useRouter } from "next/router"

import {
  mapTransportMetricToRecordData,
  useCopyMetricCatalogEntryTransport,
} from "@/metrics/catalog"
import { hydrateMetricRecord } from "@/metrics/hydration"
import type { MetricDirectoryEntry } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"
import { useScopedStorageKey } from "@/workspaces/scope"
import { toast } from "@coval/ui"

import type { MetricResource } from "@/types_api/backend/transport/create_metric_response"

interface MetricCatalogSelectionOptions {
  selectionKey?: "name" | "id"
  enabled: boolean
  pickerOpen: boolean
  setupKey: string
  byManager: Record<string, MetricDirectoryEntry>
  checkEligibility: (metric: MetricRecord) => {
    result: boolean
    error_message: string
  }
  setDraftIds: Dispatch<SetStateAction<string[]>>
  cancelPicker: () => void
}

export function useMetricCatalogSelectionTransport({
  selectionKey = "name",
  enabled,
  pickerOpen,
  setupKey,
  byManager,
  checkEligibility,
  setDraftIds,
  cancelPicker,
}: MetricCatalogSelectionOptions) {
  const router = useRouter()
  const scopeKey = useScopedStorageKey("metric-catalog-selection")
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [copies, setCopies] = useState<{
    scopeKey: string | undefined
    records: MetricResource[]
  }>({ scopeKey, records: [] })
  const generationRef = useRef(0)
  const previousScopeRef = useRef(scopeKey)
  const previousSetupRef = useRef(setupKey)
  const eligibilityRef = useRef(checkEligibility)
  const directoryRef = useRef(byManager)
  const { copyMetricCatalogEntry } = useCopyMetricCatalogEntryTransport()

  useEffect(() => {
    eligibilityRef.current = checkEligibility
    directoryRef.current = byManager
  }, [byManager, checkEligibility])

  const closeCatalog = useCallback(() => {
    generationRef.current += 1
    setCatalogOpen(false)
  }, [])

  useEffect(() => {
    closeCatalog()
    const scopeChanged =
      previousScopeRef.current !== undefined &&
      previousScopeRef.current !== scopeKey
    const setupChanged = previousSetupRef.current !== setupKey
    previousScopeRef.current = scopeKey
    previousSetupRef.current = setupKey
    if (enabled && (scopeChanged || setupChanged)) {
      cancelPicker()
    }
    const invalidatePicker = () => {
      closeCatalog()
      cancelPicker()
    }
    router.events.on("routeChangeStart", invalidatePicker)
    return () => {
      router.events.off("routeChangeStart", invalidatePicker)
      generationRef.current += 1
    }
  }, [
    cancelPicker,
    closeCatalog,
    enabled,
    pickerOpen,
    router.events,
    scopeKey,
    setupKey,
  ])

  const openCatalog = useCallback(() => {
    if (enabled && pickerOpen && scopeKey) {
      setCatalogOpen(true)
    }
  }, [enabled, pickerOpen, scopeKey])

  const addCatalogMetric = useCallback(
    async (entryId: string, presetKey?: string) => {
      const generation = generationRef.current
      const metric = await (presetKey === undefined
        ? copyMetricCatalogEntry(entryId)
        : copyMetricCatalogEntry(entryId, presetKey))
      if (generation !== generationRef.current) {
        return
      }
      const copy = hydrateMetricRecord(
        mapTransportMetricToRecordData(metric, directoryRef.current),
      )
      setCopies((current) => ({
        scopeKey,
        records: [
          ...(current.scopeKey === scopeKey ? current.records : []),
          metric,
        ],
      }))
      const eligibility = eligibilityRef.current(copy)
      if (eligibility.result) {
        setDraftIds((ids) => [
          ...new Set([
            ...ids,
            selectionKey === "id" ? copy.id : copy.metric_name,
          ]),
        ])
      } else {
        toast.info({
          title: "Metric added to workspace",
          description: `Not selected for this setup: ${eligibility.error_message}`,
        })
      }
    },
    [copyMetricCatalogEntry, scopeKey, selectionKey, setDraftIds],
  )

  const copiedMetrics = useMemo(
    () =>
      copies.scopeKey === scopeKey
        ? copies.records.map((metric) =>
            hydrateMetricRecord(
              mapTransportMetricToRecordData(metric, byManager),
            ),
          )
        : [],
    [byManager, copies, scopeKey],
  )

  return {
    catalogOpen: enabled && pickerOpen && catalogOpen,
    copiedMetrics,
    canOpenCatalog: enabled && pickerOpen && Boolean(scopeKey),
    openCatalog,
    closeCatalog,
    addCatalogMetric,
  }
}
